import React, { useRef } from "react";
import { Circle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * PhotoCaptureMode - Photo capture component
 * Handles photo capture logic and UI
 */
const PhotoCaptureMode = ({
	videoRef,
	canvasRef,
	facingMode,
	onCapture,
	onUserInteraction,
	isInitializing,
	hasPermission,
}) => {
	const capturePhoto = () => {
		if (!videoRef.current || !canvasRef.current) {
			return;
		}

		const video = videoRef.current;
		const canvas = canvasRef.current;

		// Always crop to 9:16 (mobile aspect ratio) first - never show full screen version
		const targetWidth = 480;
		const targetHeight = 854; // 480 * 16/9
		canvas.width = targetWidth;
		canvas.height = targetHeight;

		const ctx = canvas.getContext("2d");

		// Calculate crop dimensions to get center crop at 9:16
		const videoAspect = video.videoWidth / video.videoHeight;
		const targetAspect = 9 / 16;

		let sourceWidth, sourceHeight, sourceX, sourceY;

		if (videoAspect > targetAspect) {
			// Video is wider - crop sides
			sourceHeight = video.videoHeight;
			sourceWidth = sourceHeight * targetAspect;
			sourceX = (video.videoWidth - sourceWidth) / 2;
			sourceY = 0;
		} else {
			// Video is taller - crop top/bottom
			sourceWidth = video.videoWidth;
			sourceHeight = sourceWidth / targetAspect;
			sourceX = 0;
			sourceY = (video.videoHeight - sourceHeight) / 2;
		}

		// Draw cropped video frame to canvas
		// Flip horizontally for front-facing camera (mirror view)
		if (facingMode === "user") {
			ctx.save();
			ctx.translate(targetWidth, 0);
			ctx.scale(-1, 1);
		}

		ctx.drawImage(
			video,
			sourceX,
			sourceY,
			sourceWidth,
			sourceHeight,
			0,
			0,
			targetWidth,
			targetHeight
		);

		// Reset transform for front-facing camera
		if (facingMode === "user") {
			ctx.restore();
		}

		// Convert canvas to blob and trigger crop interface
		canvas.toBlob(
			(blob) => {
				const imageUrl = URL.createObjectURL(blob);
				onCapture(imageUrl);
			},
			"image/jpeg",
			0.95
		);
	};

	return (
		<div className="absolute inset-0 flex items-center justify-center">
			{/* Capture button - centered above bottom controls */}
			<div className="absolute bottom-24 left-1/2 -translate-x-1/2 transform sm:bottom-28">
				<Button
					onClick={() => {
						capturePhoto();
						if (onUserInteraction) {
							onUserInteraction();
						}
					}}
					disabled={isInitializing || !hasPermission}
					size="lg"
					className="h-14 w-14 rounded-full bg-white p-0 shadow-lg hover:bg-gray-100 disabled:opacity-50 sm:h-16 sm:w-16"
				>
					<Circle size={50} className="fill-black stroke-gray-200 stroke-1" />
				</Button>
			</div>
		</div>
	);
};

export default PhotoCaptureMode;
